import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { Document, Page, pdfjs } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Loader2, Minus, Plus, RotateCwSquare, Search, X } from "lucide-react";
import { PdfSelectionToolbar } from "./PdfSelectionToolbar";
import { HighlightOverlay } from "./HighlightOverlay";
import { PdfSourceIndicator } from "@/components/papers/PdfSourceIndicator";
import { usePdfSearch } from "@/hooks/usePdfSearch";
import type { PdfSearchMatch } from "@/hooks/usePdfSearch";
import type { PaperHighlight } from "@/hooks/useHighlights";
import {
  PDFJS_CMAP_URL,
  PDFJS_STANDARD_FONT_DATA_URL,
  PDFJS_WORKER_SRC,
} from "@/lib/pdfjs-assets";

/**
 * Safari bug workaround: getBoundingClientRect() returns height=0 for elements
 * with very small font sizes (e.g. 1px). pdfjs's TextLayer relies on measuring
 * a 1px "X" div to compute its internal #minFontSize calibration value.
 * When the measurement returns 0, ALL text spans get font-size: 0px and become
 * invisible/unselectable.
 *
 * Fix: detect the exact measurement pattern and extrapolate from a larger size.
 * Must run before pdfjs TextLayer.render() is called for the first time.
 */
const _origGetBCR = Element.prototype.getBoundingClientRect;
Element.prototype.getBoundingClientRect = function () {
  const rect = _origGetBCR.call(this);
  if (
    rect.height === 0 &&
    this instanceof HTMLDivElement &&
    this.style.fontSize === "1px" &&
    this.style.lineHeight === "1" &&
    this.textContent === "X"
  ) {
    // Measure at 10px and scale down to get the correct 1px height
    const origFS = this.style.fontSize;
    this.style.fontSize = "10px";
    const largerRect = _origGetBCR.call(this);
    this.style.fontSize = origFS;
    const correctedHeight = largerRect.height / 10;
    return new DOMRect(rect.x, rect.y, rect.width, correctedHeight || 1);
  }
  return rect;
};

pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;

/** Safari detection — needed for cross-page selection constraint (Safari-only bug). */
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const documentOptions = {
  cMapUrl: PDFJS_CMAP_URL,
  cMapPacked: true,
  standardFontDataUrl: PDFJS_STANDARD_FONT_DATA_URL,
};

/**
 * Inline styles to ensure the react-pdf text layer is selectable.
 * Tailwind's base reset can interfere with the text layer overlay.
 */
const textLayerOverrides = `
  .react-pdf__Page {
    position: relative !important;
  }
  .react-pdf__Page__textContent.textLayer {
    position: absolute !important;
    inset: 0 !important;
    z-index: 2 !important;
    overflow: hidden !important;
    user-select: text !important;
    -webkit-user-select: text !important;
    pointer-events: auto !important;
  }
  .react-pdf__Page__textContent.textLayer span {
    color: transparent !important;
    cursor: text !important;
    user-select: text !important;
    -webkit-user-select: text !important;
    pointer-events: auto !important;
  }
  /* Whitelist approach for ::selection: make ALL elements inside the page
     transparent, then re-enable only on text spans.  Safari renders
     ::selection on container divs, endOfContent, and other elements between
     spans during cross-page drags, causing a lighter blue flood.  By
     blanket-hiding ::selection and whitelisting only spans, only the actual
     selected text shows the highlight — the flood becomes invisible. */
  .react-pdf__Page::selection,
  .react-pdf__Page *::selection {
    background: transparent !important;
  }
  .react-pdf__Page__textContent.textLayer span::selection {
    background: rgba(0, 100, 255, 0.3) !important;
  }
  .react-pdf__Page canvas {
    display: block;
  }
  .pdf-search-highlight {
    background: rgba(255, 200, 0, 0.2) !important;
    color: transparent !important;
    border-radius: 1px;
    padding: 2px 0;
    /* Extend highlight left/right to bridge justification gaps between spans */
    box-shadow: 4px 0 0 rgba(255, 200, 0, 0.2), -2px 0 0 rgba(255, 200, 0, 0.2);
  }
  .pdf-search-current {
    background: rgba(255, 160, 0, 0.3) !important;
    box-shadow: 4px 0 0 rgba(255, 160, 0, 0.3), -2px 0 0 rgba(255, 160, 0, 0.3);
  }
  /* During active text selection (mousedown→mouseup), promote the text layer
     above the annotation layer so annotation sections don't intercept pointer
     events.  This lets pdf.js's .selecting class activate reliably, which
     triggers the .endOfContent gap-fill mechanism for continuous selection.
     When NOT selecting, annotations stay on top so PDF links remain clickable. */
  .pdf-selecting .textLayer {
    z-index: 4 !important;
  }
  .pdf-selecting .annotationLayer {
    pointer-events: none !important;
  }
  /* Hide pdf.js native highlight annotations — we render our own via HighlightOverlay.
     Keep links and other annotation types visible. */
  .annotationLayer .highlightAnnotation {
    display: none !important;
  }
  /* Ensure link annotations (citation refs) have no visual background —
     they must remain clickable but fully transparent so they don't darken
     our highlight overlays on lines containing references. */
  .annotationLayer .linkAnnotation,
  .annotationLayer .linkAnnotation > a {
    background: transparent !important;
    background-color: transparent !important;
    background-image: none !important;
  }
`;

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Page number input that commits on Enter/blur instead of every keystroke. */
function PageInput({ currentPage, numPages, onNavigate }: { currentPage: number; numPages: number; onNavigate: (page: number) => void }) {
  const [draft, setDraft] = useState(String(currentPage));
  const inputRef = useRef<HTMLInputElement>(null);
  // The page the draft was last synced to. Skipping when unchanged keeps the
  // effect from firing on mount: a mount-time setDraft is redundant (the draft
  // was initialised from currentPage) and, when the mount effect flushes late,
  // it overwrote a value typed in the meantime (flaky test, 2026-09-30).
  const syncedPage = useRef(currentPage);

  // Sync draft when currentPage changes externally (scroll, arrow keys)
  useEffect(() => {
    if (syncedPage.current === currentPage) return;
    syncedPage.current = currentPage;
    if (document.activeElement !== inputRef.current) {
      setDraft(String(currentPage));
    }
  }, [currentPage]);

  const commit = () => {
    const val = parseInt(draft, 10);
    if (!isNaN(val) && val >= 1 && val <= numPages) {
      onNavigate(val);
    }
    // Reset draft to actual page (clamps out-of-range or empty input)
    setDraft(String(!isNaN(val) && val >= 1 && val <= numPages ? val : currentPage));
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={draft}
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
      onKeyDown={(e) => { if (e.key === "Enter") { commit(); (e.target as HTMLInputElement).blur(); } }}
      onBlur={commit}
      onFocus={(e) => e.target.select()}
      className="w-10 rounded border bg-transparent px-1 text-center text-xs"
      aria-label="Go to page"
    />
  );
}

/**
 * Build a customTextRenderer that highlights search matches.
 * react-pdf calls this once per text item; we track cumulative char offset
 * across items to map page-level match indices to per-item ranges.
 *
 * In continuous scroll one renderer serves every rendered page, and pages
 * render interleaved — so matches are filtered by the item's own page and the
 * offset is tracked per page (2026-09-27: a single shared offset + the current
 * page's matches painted highlights on the wrong page).
 */
export function makeCustomTextRenderer(
  matches: PdfSearchMatch[],
  currentMatch: PdfSearchMatch | null,
) {
  const matchesByPage = new Map<number, PdfSearchMatch[]>();
  for (const m of matches) {
    const list = matchesByPage.get(m.pageNum) ?? [];
    list.push(m);
    matchesByPage.set(m.pageNum, list);
  }
  const offsets = new Map<number, number>();

  return (props: { str: string; itemIndex: number; pageIndex: number; pageNumber: number }) => {
    const { str, itemIndex, pageNumber } = props;

    // Items restart at 0 each time a page (re)renders
    const cumulativeOffset = itemIndex === 0 ? 0 : (offsets.get(pageNumber) ?? 0);
    const pageMatches = matchesByPage.get(pageNumber) ?? [];

    const itemStart = cumulativeOffset;
    const itemEnd = itemStart + str.length;
    offsets.set(pageNumber, itemEnd);

    if (pageMatches.length === 0) return escapeHtml(str);

    // Find matches that overlap this text item
    const overlapping: { start: number; end: number; isCurrent: boolean }[] = [];
    for (const m of pageMatches) {
      const mEnd = m.startIndex + m.length;
      if (m.startIndex < itemEnd && mEnd > itemStart) {
        const isCurrent = currentMatch !== null &&
          m.pageNum === currentMatch.pageNum &&
          m.startIndex === currentMatch.startIndex &&
          m.length === currentMatch.length;
        overlapping.push({
          start: Math.max(0, m.startIndex - itemStart),
          end: Math.min(str.length, mEnd - itemStart),
          isCurrent,
        });
      }
    }

    if (overlapping.length === 0) return escapeHtml(str);

    // Sort by start position
    overlapping.sort((a, b) => a.start - b.start);

    // Build HTML with highlighted segments
    let html = "";
    let pos = 0;
    for (const seg of overlapping) {
      if (seg.start > pos) {
        html += escapeHtml(str.slice(pos, seg.start));
      }
      const cls = seg.isCurrent
        ? "pdf-search-highlight pdf-search-current"
        : "pdf-search-highlight";
      html += `<mark class="${cls}">${escapeHtml(str.slice(seg.start, seg.end))}</mark>`;
      pos = seg.end;
    }
    if (pos < str.length) {
      html += escapeHtml(str.slice(pos));
    }
    return html;
  };
}

interface Props {
  url: string;
  onAppendToAbstract?: (text: string) => void;
  onAddAsChunk?: (text: string) => void;
  onExplain?: (text: string) => void;
  /**
   * Called when user selects text and clicks the Evidence toolbar button.
   * Receives structured {text, page, rects} so the stash handler can persist
   * a PaperHighlight without losing rects (see docs/superpowers/plans/
   * 2026-04-19-evidence-attach-rects.md).
   */
  onStashEvidence?: (data: { text: string; page: number; rects: Array<{ x: number; y: number; width: number; height: number; pageIndex: number }> | null }) => void;
  initialPage?: number;
  /** Reactive search query — triggers a new search whenever it changes. */
  searchQuery?: string;
  highlights?: PaperHighlight[];
  selectedIds?: string[];
  focusedHighlightId?: string | null;
  /** Called when user clicks a highlight overlay rect to toggle selection. */
  onToggleHighlight?: (highlightId: string) => void;
  /** Called when user deletes a highlight via the overlay popover. */
  onDeleteHighlight?: (highlightId: string) => void;
  /** Called when user selects text and clicks the Highlight toolbar button. */
  onHighlight?: (data: { text: string; page: number; rects: Array<{ x: number; y: number; width: number; height: number; pageIndex: number }> | null }) => void;
  /** External page jump trigger — { page: 0-based index, key: unique value to re-trigger same page }. */
  jumpToPage?: { page: number; key: number } | null;
  /** Render all pages in a continuous vertical scroll with windowed rendering. */
  continuousScroll?: boolean;
  /** Called whenever the displayed page changes. */
  onPageChange?: (page: number) => void;
  /** Called with the document's page count once it loads. */
  onNumPages?: (n: number) => void;
  /** Compact label shown in the toolbar, e.g. "Smith et al., 2021" */
  paperLabel?: string;
  /** Source of the current PDF — surfaces a "Your uploaded copy" badge for user uploads. */
  pdfSource?: "user" | "canonical" | "none";
}

interface SelectionState {
  text: string;
  x: number;
  y: number;
  /** Full native selection text (may be longer than text for cross-page selections). */
  nativeText?: string;
}

export function PdfViewer({ url, onAppendToAbstract, onAddAsChunk, onExplain, onStashEvidence, initialPage, searchQuery, highlights, selectedIds, focusedHighlightId, onToggleHighlight, onDeleteHighlight, onHighlight, jumpToPage, continuousScroll, onPageChange, onNumPages, paperLabel, pdfSource }: Props) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage ?? 1);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [showFindBar, setShowFindBar] = useState(false);
  const pageSizes = useRef<Map<number, { width: number; height: number }>>(new Map());
  const [defaultPageSize, setDefaultPageSize] = useState<{ width: number; height: number } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1.0); // 1.0 = fit to container width
  // Per-page user rotation (absolute 0/90/180/270). Map presence = user-rotated;
  // untouched pages get rotate=undefined so pdf.js keeps the page's own /Rotate.
  // Session-only by design (see 2026-07-18 rotation spec).
  const [rotations, setRotations] = useState<Map<number, number>>(new Map());
  const intrinsicRotations = useRef<Map<number, number>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const findInputRef = useRef<HTMLInputElement>(null);
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSearchQuery = useRef<string | undefined>(undefined);
  const initialPageRef = useRef(initialPage ?? 1);
  // Ref so a new callback identity doesn't churn onDocumentLoadSuccess.
  const onNumPagesRef = useRef(onNumPages);
  onNumPagesRef.current = onNumPages;
  initialPageRef.current = initialPage ?? 1;

  const search = usePdfSearch();

  // Cleanup programmatic scroll timer on unmount
  useEffect(() => () => clearTimeout(programmaticScrollTimer.current), []);

  // Measure container width and track resizes
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Initial measurement
    setContainerWidth(el.clientWidth);
    setContainerHeight(el.clientHeight);

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // Scroll to a page in continuous-scroll mode, suppressing the IntersectionObserver
  // long enough for PDF page content to render and settle (avoids scroll-drift).
  const scrollToPageSuppressed = useCallback((page: number) => {
    clearTimeout(programmaticScrollTimer.current);
    isProgrammaticScroll.current = true;

    const doScroll = () => {
      const el = containerRef.current?.querySelector(`[data-page-number="${page}"]`);
      el?.scrollIntoView({ behavior: "instant", block: "start" });
    };

    // Scroll immediately, then re-scroll after 500ms to correct any drift from
    // page content rendering (placeholder heights → actual heights).
    requestAnimationFrame(doScroll);
    programmaticScrollTimer.current = setTimeout(() => {
      doScroll();
      // Release observer after the corrective scroll settles
      requestAnimationFrame(() => {
        isProgrammaticScroll.current = false;
      });
    }, 500);
  }, []);

  // External page jump — set currentPage FIRST so the target renders, then scroll
  useEffect(() => {
    if (jumpToPage == null || !numPages) return;
    const page = Math.max(1, Math.min(jumpToPage.page + 1, numPages));

    isProgrammaticScroll.current = true;
    setCurrentPage(page);

    if (continuousScroll) {
      scrollToPageSuppressed(page);
    } else {
      isProgrammaticScroll.current = false;
    }
  }, [jumpToPage, numPages, continuousScroll, scrollToPageSuppressed]);

  const onDocumentLoadSuccess = useCallback(
    (pdf: PDFDocumentProxy) => {
      setNumPages(pdf.numPages);
      onNumPagesRef.current?.(pdf.numPages);
      // Clamp to actual page count — initialPageRef may be stale from a previous longer document
      const targetPage = Math.min(initialPageRef.current, pdf.numPages);
      setCurrentPage(targetPage);
      setLoadError(null);
      search.extractText(pdf);

      // In continuous scroll, scroll to the initial page and suppress the observer
      // long enough for page content to render (avoids scroll-drift from placeholder
      // heights changing as actual PDF pages load).
      if (continuousScroll && targetPage > 1) {
        scrollToPageSuppressed(targetPage);
      }
    },
    [search.extractText, continuousScroll, scrollToPageSuppressed],
  );

  const onDocumentLoadError = useCallback((error: Error) => {
    const msg = error.message || "Failed to load PDF";
    setLoadError(msg);
    toast.error("PDF failed to load", {
      description: msg.includes("404")
        ? "The PDF file was not found. Try downloading it again."
        : msg,
    });
  }, []);

  // Report page changes to parent (debounced to avoid re-render thrash during scroll).
  // Skip until PDF is loaded — the IntersectionObserver can fire before the document
  // loads, which would poison the parent's pdfCurrentPage.
  useEffect(() => {
    if (!onPageChange || !numPages) return;
    const timer = setTimeout(() => onPageChange(currentPage), 150);
    return () => clearTimeout(timer);
  }, [currentPage, onPageChange, numPages]);

  // Reactive search: trigger whenever searchQuery changes (after text is extracted)
  useEffect(() => {
    if (!numPages) return; // PDF not loaded yet
    if (searchQuery === lastSearchQuery.current) return; // no change
    lastSearchQuery.current = searchQuery;
    if (searchQuery) {
      search.setQuery(searchQuery);
      setShowFindBar(true);
    } else {
      search.setQuery("");
      setShowFindBar(false);
    }
  }, [numPages, searchQuery, search.setQuery]);

  // Scroll to top of the PDF container whenever the page changes (single-page mode only)
  useEffect(() => {
    if (continuousScroll) return;
    const el = containerRef.current;
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo(0, 0);
    }
  }, [currentPage, continuousScroll]);

  // Navigate to page when search match changes. In continuous scroll the view
  // must actually scroll there too — setting currentPage alone left the user on
  // the page they were reading (2026-09-27).
  useEffect(() => {
    if (search.currentMatch && search.currentMatch.pageNum !== currentPage) {
      setCurrentPage(search.currentMatch.pageNum);
      if (continuousScroll) scrollToPageSuppressed(search.currentMatch.pageNum);
    }
  }, [search.currentMatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Text selection detection.
  //
  // All modes: on mousedown, add .pdf-selecting to the page wrapper so the
  // text layer is promoted above the annotation layer (z-index 4 > 3).  This
  // lets pdf.js's .selecting class activate reliably, which triggers the
  // .endOfContent gap-fill mechanism for gap-free selections.  On mouseup,
  // remove .pdf-selecting so annotation links become clickable again.
  //
  // Continuous scroll + Safari only: constrain selection to one page.  Safari
  // Safari floods ::selection paint across pages during cross-page drags and
  // no CSS property can prevent it (tested: user-select, visibility, display,
  // contain, ::selection transparency).  Instead we handle it at the JS level:
  // capture the single-page selection during the drag via selectionchange, and
  // on mouseup clear the native selection (removing the flood) and show the
  // toolbar with the captured text.
  //
  // All browsers: on mousedown, .pdf-selecting promotes the text layer above
  // annotations so pdf.js's .selecting + .endOfContent mechanism works.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const safariContinuous = continuousScroll && isSafari;
    let isDragging = false;

    // Capture the last valid single-page selection during a drag.
    // Only updates when the selection is within one page — once it crosses
    // the boundary, captures stop and we keep the last good value.
    let capturedText = "";
    let capturedRect: DOMRect | null = null;
    let capturedScrollTop = 0;

    const handleSelectionChange = () => {
      if (!isDragging || !safariContinuous) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      try {
        const range = sel.getRangeAt(0);
        const startNode = range.startContainer instanceof HTMLElement
          ? range.startContainer : range.startContainer.parentElement;
        const endNode = range.endContainer instanceof HTMLElement
          ? range.endContainer : range.endContainer.parentElement;
        const startPage = startNode?.closest?.(".pdf-page");
        const endPage = endNode?.closest?.(".pdf-page");
        const samePage = startPage && endPage && startPage === endPage;
        const text = sel.toString().trim();
        // Only capture when selection stays within a single page
        if (samePage) {
          if (text && text.length >= 3) {
            capturedText = text;
            const rects = range.getClientRects();
            capturedRect = rects.length > 0 ? rects[rects.length - 1] : range.getBoundingClientRect();
            capturedScrollTop = el.scrollTop;
          }
        }
      } catch { /* ignore */ }
    };

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      capturedText = "";
      capturedRect = null;
      const isToolbar = !!(e.target as HTMLElement).closest("[data-testid='pdf-selection-toolbar']");

      const isHighlightArea = !!(e.target as HTMLElement).closest("[data-highlight-overlay]");
      if (isHighlightArea) {
        // Clicking highlight rects or popover: restore normal z-index so the
        // overlay (z-index 3) stays above text layer (z-index 2).
        el.querySelectorAll(".pdf-selecting").forEach((w) => w.classList.remove("pdf-selecting"));
      } else if (!isToolbar) {
        // Starting a new text selection: clean up previous and promote text layer
        el.querySelectorAll(".pdf-selecting").forEach((w) => w.classList.remove("pdf-selecting"));
        const pageWrapper = (e.target as HTMLElement).closest("[data-page-number]") ??
                            (e.target as HTMLElement).closest(".react-pdf__Page")?.parentElement;
        if (pageWrapper) {
          pageWrapper.classList.add("pdf-selecting");
        }
        setSelection(null);
      }
      // When clicking toolbar, keep .pdf-selecting unchanged so z-index is
      // preserved and getClientRects() returns the same rects as at mouseup.
    };

    const handleMouseUp = () => {
      isDragging = false;
      // Keep .pdf-selecting on the page wrapper — removing it would change the
      // text layer z-index, triggering a re-composite that clears the ::selection
      // highlight in Safari.  Cleanup happens on the next mousedown instead.

      const sel = window.getSelection();
      const selText = sel?.toString().trim() ?? "";
      let text = selText;
      let rect: DOMRect | null = null;

      // In Safari continuous mode, check if the selection crossed page boundaries.
      // If so, use the captured (pre-boundary) text and clear the native selection
      // to remove the visual flood.
      let crossedBoundary = false;
      if (safariContinuous && sel && sel.rangeCount > 0) {
        try {
          const range = sel.getRangeAt(0);
          const startNode = range.startContainer instanceof HTMLElement
            ? range.startContainer : range.startContainer.parentElement;
          const endNode = range.endContainer instanceof HTMLElement
            ? range.endContainer : range.endContainer.parentElement;
          const startPage = startNode?.closest?.(".pdf-page");
          const endPage = endNode?.closest?.(".pdf-page");
          if (startPage && endPage && startPage !== endPage) {
            crossedBoundary = true;
          }
        } catch { /* ignore */ }
      }

      // Track which scrollTop to use for toolbar positioning
      let scrollTopForToolbar = el.scrollTop;

      if (crossedBoundary && capturedText.length >= 3) {
        // Use captured (single-page) text for toolbar display, but position
        // the toolbar at the end of the drag (where the user is looking).
        text = capturedText;
        try {
          const range = sel?.getRangeAt(0);
          if (range) {
            const rects = range.getClientRects();
            rect = rects.length > 0 ? rects[rects.length - 1] : range.getBoundingClientRect();
          }
        } catch { /* fall back to captured rect */ }
        if (!rect) {
          rect = capturedRect;
          scrollTopForToolbar = capturedScrollTop;
        }
      } else if (text.length >= 3) {
        try {
          const range = sel?.getRangeAt(0);
          if (range) {
            const rects = range.getClientRects();
            rect = rects.length > 0 ? rects[rects.length - 1] : range.getBoundingClientRect();
          }
        } catch { /* ignore */ }
      }

      // Fall back to captured selection if current is empty
      if ((!text || text.length < 3) && capturedText.length >= 3) {
        text = capturedText;
        rect = capturedRect;
      }

      if (!text || text.length < 3 || !rect) {
        // Plain click — no selection survives this mouseup, so there is no
        // Safari ::selection to preserve.  Restore the at-rest z-order now;
        // leaving .pdf-selecting here keeps the text layer (z-index 4) above
        // the HighlightOverlay rects (z-index 3), making existing highlights
        // unclickable until some future mousedown happens to land on the
        // overlay — which it can't, because the promoted text layer wins
        // hit-testing.  (Selections ≥3 chars still keep the class until the
        // next mousedown; removing it at mouseup would re-composite and clear
        // Safari's visible selection.)
        el.querySelectorAll(".pdf-selecting").forEach((w) => w.classList.remove("pdf-selecting"));
        return;
      }

      // Store the full native selection text for cross-page highlights
      const nativeText = sel?.toString().trim() ?? text;

      const containerRect = el.getBoundingClientRect();

      // Position toolbar above the selection, clamped to visible viewport.
      const toolbarWidth = 320;
      const rawX = rect.left - containerRect.left + rect.width / 2 - toolbarWidth / 2;
      const clampedX = Math.max(4, Math.min(rawX, containerRect.width - toolbarWidth - 4));
      const rawY = rect.top - containerRect.top + scrollTopForToolbar - 40;
      const viewTop = el.scrollTop + 4;
      const viewBottom = el.scrollTop + containerRect.height - 50;
      const toolbarY = Math.max(viewTop, Math.min(rawY, viewBottom));
      setSelection({
        text,
        nativeText,
        x: clampedX,
        y: toolbarY,
      });
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    el.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mousedown", handleMouseDown);

    return () => {
      el.querySelectorAll(".pdf-selecting").forEach((w) => w.classList.remove("pdf-selecting"));
      document.removeEventListener("selectionchange", handleSelectionChange);
      el.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentPage((prev) => {
      const page = Math.max(1, prev - 1);
      if (continuousScroll && page !== prev) {
        isProgrammaticScroll.current = true;
        requestAnimationFrame(() => {
          containerRef.current
            ?.querySelector(`[data-page-number="${page}"]`)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
          requestAnimationFrame(() => { isProgrammaticScroll.current = false; });
        });
      }
      return page;
    });
  }, [continuousScroll]);

  const goToNext = useCallback(() => {
    setCurrentPage((prev) => {
      const page = Math.min(numPages ?? prev, prev + 1);
      if (continuousScroll && page !== prev) {
        isProgrammaticScroll.current = true;
        requestAnimationFrame(() => {
          containerRef.current
            ?.querySelector(`[data-page-number="${page}"]`)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
          requestAnimationFrame(() => { isProgrammaticScroll.current = false; });
        });
      }
      return page;
    });
  }, [numPages, continuousScroll]);

  const openFindBar = useCallback(() => {
    setShowFindBar(true);
    // Focus the input after render
    setTimeout(() => findInputRef.current?.focus(), 0);
  }, []);

  const closeFindBar = useCallback(() => {
    setShowFindBar(false);
    search.clearSearch();
  }, [search.clearSearch]);

  // Keyboard navigation: arrow keys for prev/next, Ctrl+F for find
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd+F: open find bar
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        openFindBar();
        return;
      }

      // Escape: close find bar
      if (e.key === "Escape" && showFindBar) {
        closeFindBar();
        return;
      }

      // Enter/Shift+Enter in find input: next/prev match
      if (e.target === findInputRef.current) {
        if (e.key === "Enter") {
          e.preventDefault();
          if (e.shiftKey) {
            search.prevMatch();
          } else {
            search.nextMatch();
          }
          return;
        }
        return; // Don't handle arrow keys when typing in find input
      }

      // Don't capture when typing in other inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (e.key === "ArrowLeft") {
        goToPrev();
      } else if (e.key === "ArrowRight") {
        goToNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToPrev, goToNext, openFindBar, closeFindBar, showFindBar, search.nextMatch, search.prevMatch]);

  // Use full container width (no wasted space), minimum 400px, with zoom
  const baseWidth = containerWidth
    ? Math.max(containerWidth, 400)
    : 800;
  const pageWidth = Math.round(baseWidth * zoomLevel);

  // Continuous scroll: determine which pages to render (visible + buffer)
  const BUFFER_PAGES = 5;
  // Track actual rendered heights per page so placeholders don't cause scroll jumps
  const pageHeights = useRef<Map<number, number>>(new Map());
  const visiblePages = useMemo(() => {
    if (!continuousScroll || !numPages) return null;
    const start = Math.max(1, currentPage - BUFFER_PAGES);
    const end = Math.min(numPages, currentPage + BUFFER_PAGES + 1);
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [continuousScroll, numPages, currentPage]);

  // Clear cached page heights when zoom changes (heights are zoom-dependent)
  useEffect(() => { pageHeights.current.clear(); }, [zoomLevel]);

  // Rotate the current page 90° CW, cycling back to the PDF's intrinsic value
  // (react-pdf's rotate prop OVERRIDES /Rotate, so equality with intrinsic
  // means "drop the override entirely").
  const rotateCurrentPage = useCallback(() => {
    // Intrinsic /Rotate is only known after the page's first onRenderSuccess.
    // Before that, cycling would base itself on a guessed 0 and could mark an
    // intrinsically-rotated page as user-rotated with no visual change.
    const intrinsic = intrinsicRotations.current.get(currentPage);
    if (intrinsic === undefined) return;
    setRotations((prev) => {
      const current = prev.get(currentPage) ?? intrinsic;
      const next = (current + 90) % 360;
      const map = new Map(prev);
      if (next === intrinsic) {
        map.delete(currentPage);
      } else {
        map.set(currentPage, next);
      }
      return map;
    });
    // Rotation changes rendered height — invalidate the placeholder cache
    pageHeights.current.delete(currentPage);
  }, [currentPage]);

  // IntersectionObserver: track which page is most visible during continuous scroll.
  // Stable deps (no visiblePages) — all page wrappers are always in the DOM as
  // placeholders, so the observer doesn't need to reconnect when the render window shifts.
  useEffect(() => {
    if (!continuousScroll || !numPages) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return;
        let mostVisible: { pageNum: number; ratio: number } | null = null;
        for (const entry of entries) {
          const pageNum = parseInt((entry.target as HTMLElement).dataset.pageNumber ?? "0", 10);
          if (pageNum > 0 && entry.intersectionRatio > (mostVisible?.ratio ?? 0)) {
            mostVisible = { pageNum, ratio: entry.intersectionRatio };
          }
        }
        if (mostVisible) {
          setCurrentPage(mostVisible.pageNum);
        }
      },
      { root: container, threshold: [0, 0.25, 0.5, 0.75, 1.0] },
    );

    const pageEls = container.querySelectorAll("[data-page-number]");
    pageEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [continuousScroll, numPages]);

  const computeFitZoom = useCallback(() => {
    if (!defaultPageSize || !containerHeight || !containerWidth) return null;
    // A quarter-turn relative to the page's intrinsic orientation swaps the
    // rendered aspect ratio (react-pdf's width prop applies to the rotated
    // viewport). Values are multiples of 90, so |Δ| % 180 === 90 ⇔ quarter-turn.
    const userRotation = rotations.get(currentPage);
    const intrinsic = intrinsicRotations.current.get(currentPage) ?? 0;
    const quarterTurned = userRotation != null && Math.abs(userRotation - intrinsic) % 180 === 90;
    const aspect = quarterTurned
      ? defaultPageSize.width / defaultPageSize.height
      : defaultPageSize.height / defaultPageSize.width;
    const bw = Math.max(containerWidth, 400);
    const renderedHeightAtFit = bw * aspect;
    if (renderedHeightAtFit > containerHeight) {
      return containerHeight / renderedHeightAtFit;
    }
    return 1.0;
  }, [defaultPageSize, containerHeight, containerWidth, rotations, currentPage]);

  const fitPage = useCallback(() => {
    const zoom = computeFitZoom();
    if (zoom != null) setZoomLevel(zoom);
  }, [computeFitZoom]);

  // Auto-fit when container size changes or defaultPageSize first becomes available
  const lastContainerHeight = useRef<number | null>(null);
  const lastPageRenderSize = useRef<boolean>(false);
  useEffect(() => {
    if (!containerHeight || !defaultPageSize) return;

    const isFirstPageRender = !lastPageRenderSize.current;
    lastPageRenderSize.current = true;

    const heightDelta = lastContainerHeight.current != null
      ? Math.abs(containerHeight - lastContainerHeight.current)
      : Infinity;
    lastContainerHeight.current = containerHeight;

    // Trigger on: first page render, or significant container height change
    if (!isFirstPageRender && heightDelta < 50) return;

    const zoom = computeFitZoom();
    if (zoom != null) {
      setZoomLevel(zoom);
    }
  }, [computeFitZoom]);

  // Rect-based selection actions are wrong on user-rotated pages —
  // computeHighlightFromSelection assumes unrotated geometry.
  const currentPageRotated = rotations.has(currentPage);
  const hasSelectionActions = onAppendToAbstract || onAddAsChunk || onExplain || onStashEvidence;

  // Computes structured highlight data (text, 0-based page, rects) from the
  // current window selection — shared by the Highlight and Evidence toolbar
  // buttons. Returns null when the selection cannot be resolved to rects.
  // Lives inside the component body to close over containerRef, pageSizes,
  // defaultPageSize, pageWidth, currentPage, continuousScroll, selection.
  type PdfRect = { x: number; y: number; width: number; height: number; pageIndex: number };
  const computeHighlightFromSelection = (text: string): { text: string; page: number; rects: PdfRect[] | null } | null => {
    const sel = window.getSelection();
    let range: Range | null = null;
    try { range = sel?.getRangeAt(0) ?? null; } catch { /* selection may have been cleared */ }
    const rects: PdfRect[] = [];

    if (range && defaultPageSize) {
      const clientRects = range.getClientRects();

      if (continuousScroll) {
        const wrapperEls = containerRef.current?.querySelectorAll("[data-page-number]");
        wrapperEls?.forEach((wrapperEl) => {
          const pageEl = wrapperEl.querySelector(".react-pdf__Page");
          if (!pageEl) return;
          const pageNum = parseInt((wrapperEl as HTMLElement).dataset.pageNumber ?? "0", 10);
          if (pageNum <= 0) return;
          const pageSize = pageSizes.current.get(pageNum) || defaultPageSize;
          if (!pageSize) return;
          const scale = pageSize.width / pageWidth;
          const pageRect = pageEl.getBoundingClientRect();
          for (let i = 0; i < clientRects.length; i++) {
            const r = clientRects[i];
            if (r.right < pageRect.left || r.left > pageRect.right ||
                r.bottom < pageRect.top || r.top > pageRect.bottom) continue;
            const clipped = {
              left: Math.max(r.left, pageRect.left),
              top: Math.max(r.top, pageRect.top),
              right: Math.min(r.right, pageRect.right),
              bottom: Math.min(r.bottom, pageRect.bottom),
            };
            const cw = clipped.right - clipped.left;
            const ch = clipped.bottom - clipped.top;
            if (cw <= 0 || ch <= 0) continue;
            const x = (clipped.left - pageRect.left) * scale;
            const width = cw * scale;
            const height = ch * scale;
            // Skip container-level rects — real text lines are < 5% of page height
            if (height > pageSize.height * 0.05) continue;
            const y = pageSize.height - ((clipped.top - pageRect.top) * scale) - height;
            rects.push({ x, y, width, height, pageIndex: pageNum - 1 });
          }
        });
      } else {
        const scale = defaultPageSize.width / pageWidth;
        const pageEl = containerRef.current?.querySelector(".react-pdf__Page");
        if (pageEl) {
          const pageRect = pageEl.getBoundingClientRect();
          for (let i = 0; i < clientRects.length; i++) {
            const r = clientRects[i];
            if (r.right < pageRect.left || r.left > pageRect.right ||
                r.bottom < pageRect.top || r.top > pageRect.bottom) continue;
            const x = (r.left - pageRect.left) * scale;
            const width = r.width * scale;
            const height = r.height * scale;
            const y = defaultPageSize.height - ((r.top - pageRect.top) * scale) - height;
            rects.push({ x, y, width, height, pageIndex: currentPage - 1 });
          }
        }
      }
    }

    const startPage = rects.length > 0 ? rects[0].pageIndex : currentPage - 1;
    const endPage = rects.length > 0 ? rects[rects.length - 1].pageIndex : startPage;

    // Cross-page selections: strip running header/footer artifacts.
    //
    // HEADER_BAND / FOOTER_BAND define what fraction of the page height
    // counts as chrome. 5% is tight enough to spare section headings and
    // first body lines (which typically start at ~8-12% from the top on
    // journal layouts) while still catching running headers/page numbers
    // (usually ≤3% tall). 8% — the previous value — was over-greedy and
    // dropped the first body line's rect on some journal templates.
    //
    // In PDF coordinates y=0 is bottom-left; y=height is top-left.
    // `r.y + r.height` = top edge of rect in PDF coords.
    const HEADER_BAND = 0.95; // rects above this ratio = header region
    const FOOTER_BAND = 0.05; // rects below this ratio = footer region
    const pages = new Set(rects.map((r) => r.pageIndex));
    if (pages.size > 1) {
      const filtered: PdfRect[] = [];
      for (const r of rects) {
        const ps = pageSizes.current.get(r.pageIndex + 1) || defaultPageSize;
        if (ps) {
          // Drop header rects on non-start pages (selection's start page
          // may legitimately begin with section heading + content near top).
          if (r.pageIndex !== startPage && (r.y + r.height) > ps.height * HEADER_BAND) continue;
          // Drop footer rects on non-end pages (selection's end page may
          // legitimately extend into the bottom of the page).
          if (r.pageIndex !== endPage && r.y < ps.height * FOOTER_BAND) continue;
        }
        filtered.push(r);
      }
      rects.length = 0;
      rects.push(...filtered);
    }

    // Build highlight text: use nativeText but strip running header/footer
    // text that slips into getSelection().toString() across page boundaries.
    // NOTE: this is a string .replace() pass — brittle when header text
    // coincidentally matches a substring in the body. A cleaner long-term
    // fix would be to derive the excerpt text from the kept rects directly,
    // not from getSelection().toString() + string replacement. Tracked as
    // follow-up; this band-tightening is the evening-scope mitigation.
    let highlightText = selection?.nativeText && selection.nativeText.length > text.length
      ? selection.nativeText : text;
    if (pages.size > 1 && continuousScroll) {
      const stripTexts: string[] = [];
      for (const pi of pages) {
        const wrapper = containerRef.current?.querySelector(`[data-page-number="${pi + 1}"]`);
        const pageEl = wrapper?.querySelector(".react-pdf__Page");
        if (!pageEl) continue;
        const ps = pageSizes.current.get(pi + 1) || defaultPageSize;
        if (!ps) continue;
        const scale = ps.width / pageWidth;
        pageEl.querySelectorAll(".textLayer span").forEach((span) => {
          const spanRect = span.getBoundingClientRect();
          const pageRect = pageEl.getBoundingClientRect();
          const spanY = ps.height - ((spanRect.top - pageRect.top) * scale) - (spanRect.height * scale);
          const spanTop = spanY + spanRect.height * scale;
          // Header band: non-start pages, top of span in top slice.
          if (pi !== startPage && spanTop > ps.height * HEADER_BAND) {
            const t = span.textContent?.trim();
            if (t) stripTexts.push(t);
          }
          // Footer band: non-end pages, bottom of span in bottom slice.
          if (pi !== endPage && spanY < ps.height * FOOTER_BAND) {
            const t = span.textContent?.trim();
            if (t) stripTexts.push(t);
          }
        });
      }
      for (const ht of stripTexts) {
        highlightText = highlightText.replace(ht, "");
      }
      highlightText = highlightText.replace(/\n{3,}/g, "\n\n").trim();
    }

    // Fail closed: if we couldn't resolve any rects, return null so callers
    // refuse to persist a rect-less highlight. The Highlight button uses
    // this guard too — rect-less highlights render nowhere on the canvas
    // and just clutter the sidebar. (Previously the function always returned
    // a structured object with rects=null, silently creating ghost rows.)
    if (rects.length === 0) return null;
    return { text: highlightText, page: startPage, rects };
  };


  // One renderer for every rendered page; it picks each page's own matches.
  const customTextRenderer = useMemo(() => {
    if (search.matches.length === 0 && !search.query) return undefined;
    return makeCustomTextRenderer(search.matches, search.currentMatch);
  }, [search.matches, search.currentMatch, search.query]);

  if (loadError) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-destructive">
          Failed to load PDF: {loadError}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <style dangerouslySetInnerHTML={{ __html: textLayerOverrides }} />

      {/* Find bar */}
      {showFindBar && (
        <div className="flex items-center gap-2 border-b px-2 py-1" data-testid="pdf-find-bar">
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            ref={findInputRef}
            type="text"
            value={search.query}
            onChange={(e) => search.setQuery(e.target.value)}
            placeholder="Find in document..."
            className="min-w-0 flex-1 bg-transparent text-xs outline-none"
            aria-label="Find in document"
          />
          {search.query && (
            <span className="shrink-0 text-xs text-muted-foreground" data-testid="match-count">
              {search.matches.length > 0
                ? `${search.currentMatchIndex + 1} of ${search.matches.length}`
                : "No matches"}
            </span>
          )}
          <Button
            variant="ghost"
            size="xs"
            onClick={search.prevMatch}
            disabled={search.matches.length === 0}
            aria-label="Previous match"
          >
            <ChevronUp className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={search.nextMatch}
            disabled={search.matches.length === 0}
            aria-label="Next match"
          >
            <ChevronDown className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={closeFindBar}
            aria-label="Close find bar"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}

      {/* Navigation + zoom */}
      {numPages && (
        <div className="flex items-center justify-between border-b px-2 py-1" data-guide-target="highlightsButton">
          {numPages > 1 ? (
            <div className="flex items-center gap-1">
              {!continuousScroll && (
                <Button variant="ghost" size="xs" onClick={goToPrev} disabled={currentPage <= 1} aria-label="Previous page">
                  <ChevronLeft className="size-4" />
                </Button>
              )}
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <PageInput
                  currentPage={currentPage}
                  numPages={numPages}
                  onNavigate={(page) => {
                    setCurrentPage(page);
                    if (continuousScroll) {
                      scrollToPageSuppressed(page);
                    }
                  }}
                />
                / {numPages}
              </span>
              {!continuousScroll && (
                <Button variant="ghost" size="xs" onClick={goToNext} disabled={currentPage >= numPages} aria-label="Next page">
                  <ChevronRight className="size-4" />
                </Button>
              )}
            </div>
          ) : <div />}
          <div className="flex min-w-0 flex-1 items-center justify-center gap-2 px-2">
            {paperLabel && (
              <span className="truncate text-xs text-subtext0" title={paperLabel}>{paperLabel}</span>
            )}
            <PdfSourceIndicator source={pdfSource} />
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="xs" onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.15))} aria-label="Zoom out">
              <Minus className="size-3.5" />
            </Button>
            <button
              onClick={() => setZoomLevel(1.0)}
              className="min-w-[3rem] text-center text-xs text-muted-foreground hover:text-foreground"
              title="Reset to fit width"
            >
              {Math.round(zoomLevel * 100)}%
            </button>
            <Button variant="ghost" size="xs" onClick={() => setZoomLevel((z) => Math.min(3.0, z + 0.15))} aria-label="Zoom in">
              <Plus className="size-3.5" />
            </Button>
            <span className="mx-1 h-4 w-px bg-border" />
            <button
              onClick={fitPage}
              className="text-xs text-muted-foreground hover:text-foreground px-1"
              title="Fit full page in view"
            >
              Fit
            </button>
            <span className="mx-1 h-4 w-px bg-border" />
            <Button variant="ghost" size="xs" onClick={rotateCurrentPage} aria-label="Rotate page" title="Rotate current page">
              <RotateCwSquare className="size-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* PDF content */}
      <div ref={containerRef} className="relative flex-1 overflow-auto">
        {selection && (hasSelectionActions || onHighlight) && (
          <PdfSelectionToolbar
            selectedText={selection.text}
            position={{ x: selection.x, y: selection.y }}
            onAppendToAbstract={onAppendToAbstract ?? (() => {})}
            onAddAsChunk={onAddAsChunk ?? (() => {})}
            onExplain={onExplain}
            onStashEvidence={onStashEvidence && !currentPageRotated ? (text) => {
              const data = computeHighlightFromSelection(text);
              if (!data) return;
              onStashEvidence(data);
              window.getSelection()?.removeAllRanges();
              containerRef.current?.querySelectorAll(".pdf-selecting").forEach((w) => w.classList.remove("pdf-selecting"));
            } : undefined}
            onHighlight={onHighlight && !currentPageRotated ? (text) => {
              const data = computeHighlightFromSelection(text);
              if (!data) return;
              onHighlight(data);
              window.getSelection()?.removeAllRanges();
              // Restore normal z-index so HighlightOverlay is clickable
              containerRef.current?.querySelectorAll(".pdf-selecting").forEach((w) => w.classList.remove("pdf-selecting"));
            } : undefined}
            onDismiss={() => setSelection(null)}
          />
        )}
        <Document
          file={url}
          options={documentOptions}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          }
        >
          {continuousScroll && visiblePages ? (
            /* Continuous scroll: render visible pages + buffer, stacked vertically */
            <>
              {Array.from({ length: numPages ?? 0 }, (_, i) => i + 1).map((pageNum) => {
                const isRendered = visiblePages.includes(pageNum);
                const estimatedHeight = pageHeights.current.get(pageNum)
                  ?? (defaultPageSize
                    ? (defaultPageSize.height / defaultPageSize.width) * pageWidth
                    : 800);
                return (
                  <div
                    key={pageNum}
                    data-page-number={pageNum}
                    className="pdf-page relative mb-2 mx-auto"
                    style={!isRendered ? { height: estimatedHeight, width: pageWidth } : { width: pageWidth }}
                    // Heights are cached ONLY in onRenderSuccess (post-canvas-render).
                    // A wrapper-ref measurement here runs on every commit and can
                    // cache mount-transient heights (collapsed/pre-fit values),
                    // which poison the placeholder cache and cause scroll jumps.
                  >
                    {isRendered && (
                      <>
                        <Page
                          pageNumber={pageNum}
                          width={pageWidth}
                          rotate={rotations.get(pageNum)}
                          customTextRenderer={customTextRenderer}
                          onRenderSuccess={(page) => {
                            const size = { width: page.originalWidth, height: page.originalHeight };
                            pageSizes.current.set(pageNum, size);
                            intrinsicRotations.current.set(pageNum, page.rotate ?? 0);
                            // Re-measure after the canvas actually rendered — the
                            // wrapper-ref measurement can race ahead of react-pdf's
                            // async render and cache a pre-rotation height.
                            const wrapperEl = containerRef.current?.querySelector(`[data-page-number="${pageNum}"]`);
                            const h = wrapperEl?.getBoundingClientRect().height;
                            if (h && h > 0) pageHeights.current.set(pageNum, h);
                            if (!defaultPageSize) setDefaultPageSize(size);
                          }}
                          loading={
                            <div className="flex items-center justify-center" style={{ height: estimatedHeight }}>
                              <Loader2 className="size-6 animate-spin text-muted-foreground" />
                            </div>
                          }
                        />
                        {highlights && highlights.length > 0 && !rotations.has(pageNum) && (() => {
                          const size = pageSizes.current.get(pageNum) || defaultPageSize;
                          if (!size) return null;
                          return (
                            <HighlightOverlay
                              highlights={highlights}
                              selectedIds={selectedIds ?? []}
                              focusedId={focusedHighlightId}
                              pageIndex={pageNum - 1}
                              scale={pageWidth / size.width}
                              pageHeight={size.height}
                              onToggle={onToggleHighlight ?? (() => {})}
                              onDelete={onDeleteHighlight}
                            />
                          );
                        })()}
                      </>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            /* Single page mode */
            <div className="relative">
              <Page
                pageNumber={currentPage}
                width={pageWidth}
                rotate={rotations.get(currentPage)}
                customTextRenderer={customTextRenderer}
                onRenderSuccess={(page) => {
                  intrinsicRotations.current.set(currentPage, page.rotate ?? 0);
                  setDefaultPageSize({ width: page.originalWidth, height: page.originalHeight });
                }}
                loading={
                  <div className="flex h-64 items-center justify-center">
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                  </div>
                }
              />
              {highlights && highlights.length > 0 && defaultPageSize && !rotations.has(currentPage) && (
                <HighlightOverlay
                  highlights={highlights}
                  selectedIds={selectedIds ?? []}
                  focusedId={focusedHighlightId}
                  pageIndex={currentPage - 1}
                  scale={pageWidth / defaultPageSize.width}
                  pageHeight={defaultPageSize.height}
                  onToggle={onToggleHighlight ?? (() => {})}
                  onDelete={onDeleteHighlight}
                />
              )}
            </div>
          )}
        </Document>
      </div>
    </div>
  );
}
